import { Injectable, EventEmitter } from '@angular/core';
import { ObjectUtils, Sorter } from '@ark-libs/ngx-utils';

import { Principal } from '../auth/principal.service';
import { CoreConfigService } from '../config/core-config.service';
import { UserStorageService } from './user-storage.service';
import { UserContextService } from './user-context.service';
import { UaaResourceService } from './uaa-resource.service';
import { UaaOfficeService } from './uaa-office.service';
import { ReadyState, ReadyStateFlag } from './ready-state.model';

@Injectable({ providedIn:'root' })
export class OfficeSwitcherService {

	private _communityGroupsMap: {[key: string]: any[]} = {};
	private _entryOffice:any;
	private _readyState : ReadyStateFlag|string;

	private _lastOffice:any;
    private _selector:any;

    currentOfficeChange: EventEmitter<any>;
    readyStateChange: EventEmitter<ReadyState> = new EventEmitter();


    constructor(
        private principal: Principal,
        private userStorage: UserStorageService,
        private userContext: UserContextService,
		private configService: CoreConfigService,
        private resourceService: UaaResourceService,
        private officeService: UaaOfficeService,
    ) {
        this.currentOfficeChange = this.userContext.currentOfficeChange;
		this.userContext.currentAppChange.subscribe(app=>{
			// console.log("OfficeSwitcherService -> currentAppChange.subscribe -> ", app);
			this.loadData();
		});

		this.userContext.currentOfficeChange.subscribe(office=>{
			// console.log("OfficeSwitcherService -> currentOfficeChange.subscribe -> ", office);
			this._lastOffice = office;
			this.loadData();
		});
        this.principal.authenticationStateChange.subscribe(account=>{
            this.loadData();
		});

		this._lastOffice = this.userContext.currentOffice;
		this.loadData();
	}

	get canOpenSelector(){
		return (this.currentOfficeLevel=='COMMUNITY_GROUP'&&this.communityGroups&&this.communityGroups.length == 1) ? false 
			: (this.currentOffice && this._selector);
	}
    registerSelector(selector:any){
        this._selector = selector;
	}
    openSelector(){
        if(this._selector){
            try{this._selector.showModal();}catch{}
        }
	}
	
	private checkReady(state?: ReadyState){
		if(state==null) state = {state:ReadyStateFlag.READY};
		// console.log("OfficeSwitcherService.checkReady() -> ", this._readyState, state);
        if(this._readyState != state.state){
            this._readyState = state.state;
            this.readyStateChange.emit(state);
        }
    }
    get isReady():boolean{
		return this._readyState == ReadyStateFlag.READY;
	}
    
    get currentOffice(): {id?:number, code?:string, name?:string, fullName?:string, officeType?:string}{
        return this.userContext.currentOffice;
	}
    get currentOfficeLevel():string{
        return this.currentOffice?this.currentOffice.officeType:null;
	}
	get currentOfficeLevelText(): string{
		switch (this.currentOfficeLevel) {
			case "COMMUNITY":
				return "小区";
			case "COMMUNITY_GROUP":
				return "集团";
			case "BRANCH_REGION":
				return "区域";
			default:
				return null;
		}
	}
	parseOfficeIcon(office?: any): string{
		if(!office) office = this.currentOffice;
		if(office){
			switch (office.officeType) {
				case 'COMMUNITY_GROUP':
					return 'anticon anticon-home';
				case 'BRANCH_REGION':
					return 'anticon anticon-appstore-o';
				case 'COMMUNITY':
					return 'anticon anticon-environment-o';
			}
		}
		return "anticon anticon-environment-o";
	}
	private get currentApp(){
		return this.userContext.currentApp;
	}
	private setCurrentOffice(office:any){
        const lastOffice = this._lastOffice;
		if(office&&office.id == (lastOffice?lastOffice.id:null)) return;

		// console.log("OfficeSwitcherService.setCurrentOffice() -> ", office, lastOffice);

        const newOffice = ObjectUtils.cloneAndRemoveObjectRef(office, 1);
        if(newOffice){
			this.setLastOfficeByType(newOffice);
			this.setLastOfficeByType(ObjectUtils.cloneAndRemoveObjectRef(office.communityGroup, 0));
			this.setLastOfficeByType(ObjectUtils.cloneAndRemoveObjectRef(office.branchRegion, 0));
		}

		this._lastOffice = newOffice;
		this.userStorage.setCurrentOffice(newOffice);
	}
	private setLastOfficeByType(office:any){
		if(!office) return;
		let officeType = office.officeType;
		this.userStorage.put("lastOffice_" + officeType, office);
		if(officeType != 'COMMUNITY_GROUP') officeType = officeType+'['+office.communityGroupId+']';
		this.userStorage.put("lastOffice_" + officeType, office);
	}
	private getLastOfficeByType(officeType:string){
		if(!officeType) return null;
		const lastCommunityGroup = this.userStorage.get("lastOffice_COMMUNITY_GROUP");
		const office = lastCommunityGroup?this.userStorage.get("lastOffice_"+officeType+'['+ lastCommunityGroup.id +']'):null;
		return office||this.userStorage.get("lastOffice_"+officeType);
	}
	
    changeCurrentOffice(office, force:boolean=false) {
		// console.log(this.currentApp,office,force);
		if(!force && ((this.currentApp?this.currentApp.bindOfficeLevel:null) != (office?office.officeType:null))) return;
		this.setCurrentOffice(office);
	}

	private loadData(){
		if(this.currentApp==null) return;

		const lastOfficeLevel = this._lastOffice?this._lastOffice.officeType:null;
		const appBindOfficeLevel = this.currentApp.bindOfficeLevel;
		if(appBindOfficeLevel != lastOfficeLevel) this._lastOffice = null;
		
        this._communityGroupsMap = {};
		this._entryOffice = null;

		this.checkReady({state:ReadyStateFlag.LOADING});

		const hasEntryOffice = !!(this.configService.entryOfficeId||this.configService.entryOfficeCode);
		// console.log("OfficeSelectorComponent.initData() -> hasEntryOffice = ",hasEntryOffice);

		if(hasEntryOffice) this.__loadData_EntryOffice();
		else this.__loadData_OfficeTree();
	}

	private __loadData_EntryOffice(){
		(async ()=>{
            const entryOfficeId = this.configService.entryOfficeId;
            const entryOfficeCode = this.configService.entryOfficeCode;

			if(entryOfficeId) await this.officeService.getOffice(entryOfficeId).then(office=>this._entryOffice=office);
			else if(entryOfficeCode) await this.officeService.getOfficeByCode(entryOfficeCode).then(office=>this._entryOffice=office);

			this.changeCurrentOffice(this._entryOffice);
			this.checkReady();
			// console.log("initByEntryOffice -> currentOfficeLevel=",this.currentOfficeLevel, "currentOffice=",this.currentOffice);
		})();
	}

	private __loadData_OfficeTree(){
        
		(async ()=>{


			// 自动获取默认的工作区
			let currentCommunityGroup:any = null;
			let currentRegion:any = null;
			let currentCommunity:any = null;
			let currentOffice:any = null;

			const level = this.currentApp?this.currentApp.bindOfficeLevel:null;
			const lastOffice = this._lastOffice||this.getLastOfficeByType(level);

			// console.log("---------------------------------------------------------------------------------");
			// console.log("OfficeSwitcherService.__loadData_OfficeTree() -> bindOfficeLevel=", level, this.currentApp);

            // console.log("OfficeSelectorComponent.initData()");
            // console.log("lastOffice=",lastOffice);
            // console.log("lastOfficeLevel=",level);

            if(['COMMUNITY_GROUP','BRANCH_REGION','COMMUNITY'].indexOf(level)!=-1){
                await this.loadMyCommunityGroups(level);
                const theCommunityGroups = this.getCommunityGroups(level);
                if(theCommunityGroups.length>0){
                    currentCommunityGroup = theCommunityGroups.filter(item=>lastOffice&&(item.id==lastOffice.id||item.id==lastOffice['communityGroupId'])).pop()||theCommunityGroups[0];
                }
                if(level=="COMMUNITY_GROUP") currentOffice = currentCommunityGroup;
                // console.log("currentCommunityGroup=",currentCommunityGroup);
            }
            if(currentCommunityGroup){
                await this.loadMyRegions(level, currentCommunityGroup);
                const deepRegions = function(region){
                    if(currentRegion==null&&(region.regions==null||region.regions.length==0)) currentRegion = region;
                    if(lastOffice&&(region.id==lastOffice['branchRegionId']||region.id==lastOffice.id)) currentRegion = region;
                    if(region.regions) region.regions.forEach(child => deepRegions(child));
                };
                currentCommunityGroup.regions.forEach(item=>deepRegions(item));
                if(level=="BRANCH_REGION") currentOffice = currentRegion;
                // console.log("currentRegion=",currentRegion);
            }

            if(currentRegion){
                await this.loadMyCommunities(level, currentRegion);
                currentCommunity = (currentRegion.communities.length==0)?null
                    :(currentRegion.communities.filter(item=>lastOffice&&item.id==lastOffice.id).pop()||currentRegion.communities[0]);
                if(level=="COMMUNITY") currentOffice = currentCommunity;
                // console.log("currentCommunity=",currentCommunity);
            }
            

            // 展开当前机构
            if(currentCommunityGroup) currentCommunityGroup.__active = true;
            if(currentCommunity) currentCommunity.__active = true;
            let parentRegion:any = currentRegion;
            while(parentRegion!=null){
                parentRegion.__active = true;
                parentRegion = parentRegion.branchRegion;
            }

			if(this._lastOffice==null) this.changeCurrentOffice(currentOffice);
			this.checkReady();

            // console.log("currentOffice=",currentOffice);
            // console.log("---------------------------------------------------------------------------------");
		})();
    }


	tryLoadChildren(office: any){
		// console.log("tryLoadChildren() -> ",this.currentOfficeLevel, office);
        if(office==null) return;
        const level = this.currentOfficeLevel;
		
        switch (office.officeType) {
            case "COMMUNITY":
            break;
			case "COMMUNITY_GROUP":
				if(level != "COMMUNITY_GROUP") this.loadMyRegions(level, office);
                break;
            case "BRANCH_REGION":
				if(level == "BRANCH_REGION"&&office.regions&&office.regions.length==0){
					//this.changeCurrentOffice(office);
				}else{
					//if(level != "COMMUNITY_GROUP")this.loadMyRegions(level, office); //地区树结构一次性加载
					if(level == "COMMUNITY") this.loadMyCommunities(level, office);
                }
                break;
        }
    }
   
    get currentCommunityGroups(){
        return this.getCommunityGroups(this.currentOfficeLevel);
    }
    private set communityGroups(groups:any[]){
        this.setCommunityGroups(this.currentOfficeLevel, groups);
    }
    private get communityGroups(){
        return this.getCommunityGroups(this.currentOfficeLevel);
    }

    private setCommunityGroups(level:string, groups:any[]){
        if(['COMMUNITY_GROUP','BRANCH_REGION','COMMUNITY'].indexOf(level)==-1) return;
        this._communityGroupsMap[level] = groups;
    }
    private getCommunityGroups(level:string){
        if(['COMMUNITY_GROUP','BRANCH_REGION','COMMUNITY'].indexOf(level)==-1) return [];
        return this._communityGroupsMap[level];
    }

	private loadMyCommunityGroups(level:string){
		if(!this.getCommunityGroups(level)){
			return this.officeService.getMyCommunityGroups({scopeBy:level}).then(data=>{
				data = data||[];
				data.forEach(item=>{if(!item.officeType) item.officeType="COMMUNITY_GROUP"});
				this.setCommunityGroups(level, data);
				return data;
			});
		}
	}
	private loadMyRegions(level:string, office:any){
		if(!(level=="BRANCH_REGION"||level=="COMMUNITY")) {
			office.regions=[];
			return;
		}
		
		if(office.regions&&!office.hasError) return;
		return this.officeService.getMyCommunityRegions(office, {deep:true,scopeBy:level}).then(data=>{
			// console.log("loadMyRegions:", data);
			const root: any = data||[];
			const regions = (Array.isArray(root)? root: root.children||root.regions||root.items)||[];
			const parseNodes = function(parent, item){
				if(!item.officeType) item.officeType="BRANCH_REGION";
				if(parent.officeType == "COMMUNITY_GROUP"){
					item.communityGroup = parent;
					item.communityGroupId = parent.id;
				}else if(parent.officeType == "BRANCH_REGION"){
					item.branchRegion = parent;
					item.branchRegionId = parent.id;
					item.communityGroup = parent.communityGroup;
					item.communityGroupId = parent.communityGroupId;
				}
				item.regions = item.children||item.regions||[];
				item.regions.forEach(child=>parseNodes(item, child));
			};
			regions.forEach(item=>parseNodes(office, item));
			office.regions = regions;
			office.hasError = false;
			office.errorMessage = null;
			return regions;
		}).catch(error=>{
			office.regions = [];
			office.hasError = true;
			office.errorMessage = error.message;
			// console.log("loadMyRegions error:", error);
			return [];
		});
	}
	private loadMyCommunities(level:string, office:any){
		if(level!="COMMUNITY") {
			office.communities=[];
			return;
		}

		if(office.communities&&!office.hasError) return;
		office.communities = [];
		return this.officeService.getMyCommunities(office, {deep:false,scopeBy:level}).then(data=>{
			// console.log("loadMyCommunities:", data);
			if(data){
				const parseNodes = function(parent, item){
					if(!item.officeType) item.officeType="COMMUNITY";
					if(parent.officeType == "COMMUNITY_GROUP"){
						item.communityGroup = parent;
						item.communityGroupId = parent.id;
					}else if(parent.officeType == "BRANCH_REGION"){
						item.branchRegion = parent;
						item.branchRegionId = parent.id;
						item.communityGroup = parent.communityGroup;
						item.communityGroupId = parent.communityGroupId;
					}
				};
				data.forEach(item=>parseNodes(office, item));
			}else{data=[];}
			office.communities = data;
			office.hasError = false;
			office.errorMessage = null;
			return data;
		}).catch(error=>{
			office.regions = [];
			office.hasError = true;
			office.errorMessage = error.message;
			// console.log("loadMyCommunities error:", error);
			return [];
		});
	}
}